import { TeButton, TeButtonOutline, TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl';

import { DeclineAgreementButton } from '../../ServiceAgreement/DeclineAgreementButton';
import { useAcceptRequestBusinessPartner } from '../../../pages/RequestBusinessPartner/businessPartnerRequest.mutations';
import { useRequestBusinessPartner } from '../../../pages/RequestBusinessPartner/businessPartnerRequest.queries';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';
import { getErrorStatus } from '../../../utils/errorStatus';

interface ReviewCompanyCreationRequestStepActionsProps {
  step: 'companyReview' | 'agreementReview' | 'termsAndConditions';
  id: string;
  onNextStep: () => void;
  onPreviousStep?: () => void;
  isNextStepDisabled?: boolean;
  onReject: () => void;
}

function ReviewCompanyCreationRequestStepActions({
  step,
  id,
  onNextStep,
  onPreviousStep,
  onReject,
  isNextStepDisabled,
}: ReviewCompanyCreationRequestStepActionsProps) {
  const { t } = useTranslation();
  const { mutateAsync: acceptRequest, isPending: isAcceptingPending } =
    useAcceptRequestBusinessPartner();
  const { data: requestBusinessPartner } = useRequestBusinessPartner(id);
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  if (!requestBusinessPartner) return;

  const handleClickAcceptAgreement = async () => {
    try {
      await acceptRequest({ id });

      onNextStep();
    } catch (error: unknown) {
      if (!showError(error)) {
        const heading = t('serviceAgreement.resultMessages.acceptErrorTitle');
        let body = t('serviceAgreement.resultMessages.acceptErrorDescription');
        // An API error response is the parsed body with the HTTP status
        // attached by the shared interceptor; its `message` is the backend's
        // explanation. Anything else (e.g. a network failure) keeps the fallback.
        if (
          getErrorStatus(error) !== undefined &&
          typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          body = error.message;
        }
        notificationService.addNotification(heading, body, Statuses.error);
      }
      return Promise.reject(error);
    }
  };

  if (step === 'companyReview')
    return (
      <TeContainer
        gap="xs"
        fill
        grow
        justifyContent="space-between"
        alignItems="center"
        padding="m"
      >
        <DeclineAgreementButton
          type="requestBusinessPartner"
          id={id}
          onReject={onReject}
          company={requestBusinessPartner}
        />
        <TeButton text={t('nextStep')} size="s" teclick={onNextStep} />
      </TeContainer>
    );

  if (step === 'agreementReview') {
    return (
      <TeContainer
        gap="xs"
        fill
        grow
        justifyContent="space-between"
        alignItems="center"
        padding="m"
      >
        <DeclineAgreementButton id={id} onReject={onReject} company={requestBusinessPartner} />
        <TeContainerSlim gap="xs">
          <TeButtonOutline
            text={t('previousStep')}
            size="s"
            variant="neutral"
            teclick={onPreviousStep}
            disabled={isAcceptingPending}
          />
          <TeButton
            text={t('nextStep')}
            size="s"
            teclick={onNextStep}
            disabled={isNextStepDisabled}
          />
        </TeContainerSlim>
      </TeContainer>
    );
  }
  return (
    <TeContainer gap="xs" fill grow justifyContent="space-between" alignItems="center" padding="m">
      <DeclineAgreementButton
        id={id}
        disabled={isAcceptingPending}
        onReject={onReject}
        company={requestBusinessPartner}
      />
      <TeContainerSlim gap="xs">
        <TeButtonOutline
          text={t('previousStep')}
          size="s"
          variant="neutral"
          teclick={onPreviousStep}
          disabled={isAcceptingPending}
        />
        <TeButton
          text={t('serviceAgreement.accept')}
          size="s"
          teclick={handleClickAcceptAgreement}
          loading={isAcceptingPending}
          disabled={isAcceptingPending || isNextStepDisabled}
        />
      </TeContainerSlim>
    </TeContainer>
  );
}

export { ReviewCompanyCreationRequestStepActions };
